import type { AnalysisResult, PngStructure, ReportDocument } from './types.js';

export const TRUE_ALPHA_TOOL_NAME = 'TrueAlpha' as const;
export const TRUE_ALPHA_TOOL_VERSION = '0.1.0';
export const REPORT_JSON_MIME_TYPE = 'application/json;charset=utf-8';
export const REPORT_CSV_MIME_TYPE = 'text/csv;charset=utf-8';

/** The report boundary intentionally accepts only the serializable core result. */
export type ReportRecord = AnalysisResult;

export interface ReportExportOptions {
  /** `version` is kept as a terse convenience for callers and tests. */
  readonly version?: string;
  readonly toolVersion?: string;
  readonly generatedAt?: string | Date;
  /** JSON indentation: true/default = 2, false = compact, or an integer 0..10. */
  readonly pretty?: boolean | number;
}

export interface ExportedAnalysisRecord {
  readonly id: string;
  readonly analyzedAt: string;
  readonly durationMs?: number;
  readonly file: AnalysisResult['file'];
  readonly png: PngStructure;
  readonly stats: AnalysisResult['pixels'];
  readonly classifications: AnalysisResult['labels'];
  readonly heuristics: {
    readonly solidBackground: AnalysisResult['solidBackground'];
    readonly checkerboard: AnalysisResult['checkerboard'];
  };
  /** Reasons from heuristics that actually produced a suspected-risk result. */
  readonly riskReasons: readonly string[];
}

export interface TrueAlphaReportDocument extends ReportDocument {
  readonly results: readonly ExportedAnalysisRecord[];
}

type ReportOptionsArgument = ReportExportOptions | string | undefined;

interface ResolvedReportOptions {
  readonly toolVersion: string;
  readonly generatedAt: string;
  readonly indentation: number | undefined;
}

function normalizeIsoDate(value: string | Date, fieldName: string): string {
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (!Number.isFinite(date.getTime())) {
    throw new TypeError(`${fieldName} must be a valid ISO date or Date object.`);
  }
  return date.toISOString();
}

function normalizeToolVersion(value: string): string {
  const version = value.trim();
  if (version.length === 0) {
    throw new TypeError('Tool version must not be empty.');
  }
  return version;
}

function resolveOptions(options: ReportOptionsArgument): ResolvedReportOptions {
  const normalized: ReportExportOptions =
    typeof options === 'string' ? { version: options } : (options ?? {});

  if (
    normalized.version !== undefined &&
    normalized.toolVersion !== undefined &&
    normalized.version !== normalized.toolVersion
  ) {
    throw new TypeError('version and toolVersion must match when both are provided.');
  }

  const pretty = normalized.pretty ?? true;
  let indentation: number | undefined;
  if (typeof pretty === 'boolean') {
    indentation = pretty ? 2 : undefined;
  } else if (Number.isInteger(pretty) && pretty >= 0 && pretty <= 10) {
    indentation = pretty === 0 ? undefined : pretty;
  } else {
    throw new TypeError('pretty must be a boolean or an integer from 0 to 10.');
  }

  return {
    toolVersion: normalizeToolVersion(
      normalized.toolVersion ?? normalized.version ?? TRUE_ALPHA_TOOL_VERSION,
    ),
    generatedAt: normalizeIsoDate(normalized.generatedAt ?? new Date(), 'generatedAt'),
    indentation,
  };
}

function cloneColor(
  color: AnalysisResult['pixels']['corners']['topLeft'],
): AnalysisResult['pixels']['corners']['topLeft'] {
  return { r: color.r, g: color.g, b: color.b, a: color.a };
}

function cloneBoundingBox(
  box: AnalysisResult['pixels']['contentBoundingBox'],
): AnalysisResult['pixels']['contentBoundingBox'] {
  if (box === null) return null;
  return {
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    left: box.left,
    top: box.top,
    right: box.right,
    bottom: box.bottom,
  };
}

function cloneMargins(
  margins: AnalysisResult['pixels']['transparentMargins'],
): AnalysisResult['pixels']['transparentMargins'] {
  return {
    top: margins.top,
    right: margins.right,
    bottom: margins.bottom,
    left: margins.left,
  };
}

function clonePixelStatistics(stats: AnalysisResult['pixels']): AnalysisResult['pixels'] {
  const dominant = stats.boundaryDominantColor;
  return {
    totalPixels: stats.totalPixels,
    fullyTransparentPixels: stats.fullyTransparentPixels,
    semiTransparentPixels: stats.semiTransparentPixels,
    fullyOpaquePixels: stats.fullyOpaquePixels,
    nonOpaquePixels: stats.nonOpaquePixels,
    transparentPercentage: stats.transparentPercentage,
    fullyTransparentPercentage: stats.fullyTransparentPercentage,
    semiTransparentPercentage: stats.semiTransparentPercentage,
    nonOpaquePercentage: stats.nonOpaquePercentage,
    minAlpha: stats.minAlpha,
    maxAlpha: stats.maxAlpha,
    averageAlpha: stats.averageAlpha,
    contentBoundingBox: cloneBoundingBox(stats.contentBoundingBox),
    boundingBox: cloneBoundingBox(stats.boundingBox),
    transparentMargins: cloneMargins(stats.transparentMargins),
    margins: cloneMargins(stats.margins),
    edgePixelCount: stats.edgePixelCount,
    edgeNonTransparentPixels: stats.edgeNonTransparentPixels,
    edgeNonTransparentRatio: stats.edgeNonTransparentRatio,
    edgeOpaqueRatio: stats.edgeOpaqueRatio,
    corners: {
      topLeft: cloneColor(stats.corners.topLeft),
      topRight: cloneColor(stats.corners.topRight),
      bottomRight: cloneColor(stats.corners.bottomRight),
      bottomLeft: cloneColor(stats.corners.bottomLeft),
    },
    boundaryDominantColor:
      dominant === null
        ? null
        : {
            color: cloneColor(dominant.color),
            quantizedColor: cloneColor(dominant.quantizedColor),
            hex: dominant.hex,
            count: dominant.count,
            total: dominant.total,
            ratio: dominant.ratio,
            percentage: dominant.percentage,
          },
  };
}

function clonePngStructure(png: PngStructure): PngStructure {
  return {
    valid: true,
    signatureValid: true,
    width: png.width,
    height: png.height,
    bitDepth: png.bitDepth,
    colorType: png.colorType,
    colorTypeName: png.colorTypeName,
    compressionMethod: png.compressionMethod,
    filterMethod: png.filterMethod,
    interlaceMethod: png.interlaceMethod,
    hasTrns: png.hasTrns,
    hasTRNS: png.hasTrns,
    hasAlphaChannel: png.hasAlphaChannel,
    supportsTransparency: png.supportsTransparency,
    chunks: [...png.chunks],
  };
}

function cloneSolidBackground(
  detection: AnalysisResult['solidBackground'],
): AnalysisResult['solidBackground'] {
  return {
    kind: 'solid-background',
    suspected: detection.suspected,
    confidence: detection.confidence,
    reasons: [...detection.reasons],
    category: detection.category,
    dominantColor: detection.dominantColor === null ? null : cloneColor(detection.dominantColor),
    dominantColorHex: detection.dominantColorHex,
    dominantRatio: detection.dominantRatio,
    opaqueRatio: detection.opaqueRatio,
    colorVariance: detection.colorVariance,
    sampleCount: detection.sampleCount,
  };
}

function cloneCheckerboard(
  detection: AnalysisResult['checkerboard'],
): AnalysisResult['checkerboard'] {
  return {
    kind: 'baked-checkerboard',
    suspected: detection.suspected,
    confidence: detection.confidence,
    reasons: [...detection.reasons],
    palette:
      detection.palette === null
        ? null
        : [cloneColor(detection.palette[0]), cloneColor(detection.palette[1])],
    paletteCoverage: detection.paletteCoverage,
    opaqueRatio: detection.opaqueRatio,
    horizontalPeriodicity: detection.horizontalPeriodicity,
    verticalPeriodicity: detection.verticalPeriodicity,
    tileSize: detection.tileSize,
    sampleCount: detection.sampleCount,
  };
}

function toExportedRecord(result: ReportRecord): ExportedAnalysisRecord {
  const analyzedAt = normalizeIsoDate(result.analyzedAt, 'analyzedAt');
  const solidBackground = cloneSolidBackground(result.solidBackground);
  const checkerboard = cloneCheckerboard(result.checkerboard);
  const riskReasons = [
    ...(solidBackground.suspected
      ? solidBackground.reasons.map((reason) => `Solid background: ${reason}`)
      : []),
    ...(checkerboard.suspected
      ? checkerboard.reasons.map((reason) => `Baked checkerboard: ${reason}`)
      : []),
  ];

  const record: ExportedAnalysisRecord = {
    id: result.id,
    analyzedAt,
    file: {
      name: result.file.name,
      size: result.file.size,
      type: result.file.type,
      ...(result.file.lastModified === undefined ? {} : { lastModified: result.file.lastModified }),
      width: result.png.width,
      height: result.png.height,
    },
    png: clonePngStructure(result.png),
    stats: clonePixelStatistics(result.pixels),
    classifications: [...result.labels],
    heuristics: { solidBackground, checkerboard },
    riskReasons,
    ...(result.durationMs === undefined ? {} : { durationMs: result.durationMs }),
  };

  assertFiniteNumbers(record, 'result');
  return record;
}

function assertFiniteNumbers(value: unknown, path: string): void {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new TypeError(`${path} must contain only finite numbers.`);
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => assertFiniteNumbers(item, `${path}[${index}]`));
    return;
  }
  if (value !== null && typeof value === 'object') {
    for (const [key, nestedValue] of Object.entries(value)) {
      assertFiniteNumbers(nestedValue, `${path}.${key}`);
    }
  }
}

function buildReport(
  results: readonly ReportRecord[],
  options: ReportOptionsArgument,
): { document: TrueAlphaReportDocument; options: ResolvedReportOptions } {
  if (!Array.isArray(results)) {
    throw new TypeError('Report results must be an array.');
  }
  const resolved = resolveOptions(options);
  const document: TrueAlphaReportDocument = {
    tool: {
      name: TRUE_ALPHA_TOOL_NAME,
      version: resolved.toolVersion,
    },
    generatedAt: resolved.generatedAt,
    results: results.map(toExportedRecord),
  };
  return { document, options: resolved };
}

/** Builds the allowlisted, image-data-free object used by both export formats. */
export function createReportDocument(
  results: readonly ReportRecord[],
  options?: ReportOptionsArgument,
): TrueAlphaReportDocument {
  return buildReport(results, options).document;
}

/** Serializes a report to JSON. No File, Blob, ArrayBuffer, pixel buffer, or URL is copied. */
export function exportReportJson(
  results: readonly ReportRecord[],
  options?: ReportOptionsArgument,
): string {
  const built = buildReport(results, options);
  return JSON.stringify(built.document, null, built.options.indentation);
}

type CsvValue = string | number | boolean | null | undefined;

function spreadsheetSafeText(value: string): string {
  // Quoting alone does not stop spreadsheet formula evaluation.
  return /^[=+\-@\t\r]/u.test(value) ? `'${value}` : value;
}

function csvCell(value: CsvValue): string {
  if (value === null || value === undefined) return '';
  let text = typeof value === 'string' ? spreadsheetSafeText(value) : String(value);
  if (/[",\r\n]/u.test(text)) {
    text = `"${text.replaceAll('"', '""')}"`;
  }
  return text;
}

function compactJson(value: unknown): string {
  return JSON.stringify(value);
}

const CSV_HEADERS = [
  'toolVersion',
  'reportGeneratedAt',
  'analysisTime',
  'fileName',
  'fileSizeBytes',
  'fileType',
  'lastModified',
  'width',
  'height',
  'bitDepth',
  'colorType',
  'colorTypeName',
  'compressionMethod',
  'filterMethod',
  'interlaceMethod',
  'hasTrns',
  'hasAlphaChannel',
  'supportsTransparency',
  'pngChunks',
  'totalPixels',
  'fullyTransparentPixels',
  'semiTransparentPixels',
  'fullyOpaquePixels',
  'nonOpaquePixels',
  'fullyTransparentPercentage',
  'semiTransparentPercentage',
  'nonOpaquePercentage',
  'minAlpha',
  'maxAlpha',
  'averageAlpha',
  'contentBoundingBox',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'edgeNonTransparentRatio',
  'cornerTopLeft',
  'cornerTopRight',
  'cornerBottomRight',
  'cornerBottomLeft',
  'boundaryDominantColor',
  'boundaryDominantPercentage',
  'classifications',
  'solidBackgroundSuspected',
  'solidBackgroundConfidence',
  'solidBackgroundCategory',
  'solidBackgroundDominantColor',
  'solidBackgroundReasons',
  'checkerboardSuspected',
  'checkerboardConfidence',
  'checkerboardPaletteCoverage',
  'checkerboardHorizontalPeriodicity',
  'checkerboardVerticalPeriodicity',
  'checkerboardTileSize',
  'checkerboardReasons',
  'riskReasons',
  'durationMs',
] as const;

function recordToCsvRow(
  record: ExportedAnalysisRecord,
  document: TrueAlphaReportDocument,
): readonly CsvValue[] {
  const stats = record.stats;
  const solid = record.heuristics.solidBackground;
  const checker = record.heuristics.checkerboard;
  return [
    document.tool.version,
    document.generatedAt,
    record.analyzedAt,
    record.file.name,
    record.file.size,
    record.file.type,
    record.file.lastModified,
    record.png.width,
    record.png.height,
    record.png.bitDepth,
    record.png.colorType,
    record.png.colorTypeName,
    record.png.compressionMethod,
    record.png.filterMethod,
    record.png.interlaceMethod,
    record.png.hasTrns,
    record.png.hasAlphaChannel,
    record.png.supportsTransparency,
    record.png.chunks.join('|'),
    stats.totalPixels,
    stats.fullyTransparentPixels,
    stats.semiTransparentPixels,
    stats.fullyOpaquePixels,
    stats.nonOpaquePixels,
    stats.fullyTransparentPercentage,
    stats.semiTransparentPercentage,
    stats.nonOpaquePercentage,
    stats.minAlpha,
    stats.maxAlpha,
    stats.averageAlpha,
    stats.contentBoundingBox === null ? null : compactJson(stats.contentBoundingBox),
    stats.transparentMargins.top,
    stats.transparentMargins.right,
    stats.transparentMargins.bottom,
    stats.transparentMargins.left,
    stats.edgeNonTransparentRatio,
    compactJson(stats.corners.topLeft),
    compactJson(stats.corners.topRight),
    compactJson(stats.corners.bottomRight),
    compactJson(stats.corners.bottomLeft),
    stats.boundaryDominantColor?.hex,
    stats.boundaryDominantColor?.percentage,
    record.classifications.join('|'),
    solid.suspected,
    solid.confidence,
    solid.category,
    solid.dominantColorHex,
    solid.reasons.join(' | '),
    checker.suspected,
    checker.confidence,
    checker.paletteCoverage,
    checker.horizontalPeriodicity,
    checker.verticalPeriodicity,
    checker.tileSize,
    checker.reasons.join(' | '),
    record.riskReasons.join(' | '),
    record.durationMs,
  ];
}

/** Serializes one row per analyzed file using RFC 4180-compatible escaping. */
export function exportReportCsv(
  results: readonly ReportRecord[],
  options?: ReportOptionsArgument,
): string {
  const { document } = buildReport(results, options);
  const lines = [
    CSV_HEADERS.map(csvCell).join(','),
    ...document.results.map((record) => recordToCsvRow(record, document).map(csvCell).join(',')),
  ];
  return lines.join('\r\n');
}

// Small compatibility aliases for UI and third-party callers.
export const exportJsonReport = exportReportJson;
export const exportCsvReport = exportReportCsv;
export const generateJsonReport = exportReportJson;
export const generateCsvReport = exportReportCsv;
export const createJsonReport = exportReportJson;
export const createCsvReport = exportReportCsv;
